import { Component, ElementRef, EventEmitter, Input, Output } from '@angular/core';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { CanColor, CanColorCtor, CanDisable, CanDisableCtor, mixinColor, mixinDisabled } from '@angular/material/core';
import { trigger } from '@angular/animations';
import { fadeInOut, expandShrinkWidthConstHeight } from '@trilean/tr3/animations';

export interface FilterOption<T> {
  value: T;
  label: string;
  group?: unknown;
  selected?: boolean;
}

interface FilterOptionState<T> extends FilterOption<T> {
  visible: boolean;
  selected: boolean;
}

export type ExpandableContentPosition = 'before' | 'after';

// Boilerplate for applying mixins.
/** @docs-private */
class ComponentBase {
  constructor(public _elementRef: ElementRef) {}
}

const _ComponentMixinBase: CanColorCtor & CanDisableCtor & typeof ComponentBase = mixinColor(
  mixinDisabled(ComponentBase),
);

@Component({
  selector: 'tr3-expandable-filter',
  templateUrl: './expandable-filter.component.html',
  styleUrls: ['./expandable-filter.component.scss'],
  inputs: ['disabled', 'color'],
  animations: [
    trigger('animWidth', expandShrinkWidthConstHeight({ delay: 250, animateChildren: false })),
    trigger('fadeInOut', fadeInOut()),
  ],
})
export class ExpandableFilterComponent<T> extends _ComponentMixinBase implements CanColor, CanDisable {
  constructor(elementRef: ElementRef) {
    super(elementRef);
  }

  @Input()
  get open(): boolean {
    return this._open;
  }
  set open(v: boolean) {
    this._open = coerceBooleanProperty(v);
  }
  private _open = false;
  static ngAcceptInputType_open: BooleanInput;

  @Output() openChange = new EventEmitter<boolean>();

  @Output() selectionChange = new EventEmitter<T[]>();

  @Input()
  set options(v: FilterOption<T>[]) {
    this.optionsState = v.map((o) => {
      const current = this.getOptionState(o.value);
      return { visible: current?.visible ?? true, selected: current?.selected ?? false, ...o };
    });
    this.updateSelection();
  }

  @Input() tooltip?: string;

  @Input() contentPosition: ExpandableContentPosition = 'before';

  set disabled(v: boolean) {
    super.disabled = v;
    if (this.disabled) {
      this.setOpen(false);
    }
  }
  static ngAcceptInputType_disabled: BooleanInput;

  optionsState: FilterOptionState<T>[] = [];

  private getOptionState(value: unknown): FilterOptionState<T> | undefined {
    return this.optionsState.find((o) => o.value === value);
  }

  setOpen(v: boolean): void {
    if (v == this._open) return;
    this._open = v;
    this.openChange.emit(v);
    if (!v) this.clearSelection();
  }

  private clearSelection(): void {
    let changed = false;
    for (const option of this.optionsState) {
      changed ||= option.selected;
      option.selected = false;
    }
    if (changed) {
      this.updateSelection();
    }
  }

  private updateSelection(): void {
    const selection = this.optionsState.filter((e) => e.selected);
    const groups = new Set(selection.map((e) => e.group));
    for (const option of this.optionsState) {
      option.visible = option.selected || !groups.has(option.group);
    }
    this.selectionChange.emit(selection.map((e) => e.value));
  }

  onSelection(option: FilterOptionState<T>, selected: boolean): void {
    if (option.selected == selected) return;
    option.selected = selected;
    this.updateSelection();
  }

  onFocusOut(): void {
    if (this.optionsState.every((e) => !e.selected)) this.setOpen(false);
  }
}
